import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import MarkRepaidDialog from '../components/admin/MarkRepaidDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Single source of truth for outstanding-loan balances + repayment dialog.
 * - Fetches `GET /api/admin/loans/outstanding` once on mount, refreshes every 60s.
 * - Powers the inline "owes $X" badge anywhere a player name is rendered.
 * - Hosts a single Mark-Repaid dialog at the provider level so the badge can
 *   be clicked from any list to settle the loan inline — no context switch.
 */
const LoanOwedContext = createContext({
    getOwed: () => 0,
    getRow: () => null,
    refresh: () => {},
    openRepay: () => {},
});

export const useLoanOwed = () => useContext(LoanOwedContext);

export const LoanOwedProvider = ({ children, enabled = true }) => {
    const [rowMap, setRowMap] = useState({});      // userId → full row
    const [active, setActive] = useState(null);    // currently-repaying row

    const fetchAll = useCallback(async () => {
        if (!enabled) return;
        try {
            const { data } = await axios.get(`${API}/admin/loans/outstanding`, { withCredentials: true });
            const next = {};
            for (const r of (data?.rows || [])) next[r.user_id] = r;
            setRowMap(next);
        } catch { /* non-fatal — badge just won't render */ }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;
        fetchAll();
        const id = setInterval(fetchAll, 60_000);
        return () => clearInterval(id);
    }, [enabled, fetchAll]);

    const openRepay = useCallback((userId) => {
        const row = rowMap[userId];
        if (row) setActive(row);
    }, [rowMap]);

    const value = useMemo(() => ({
        getOwed: (userId) => rowMap[userId]?.outstanding || 0,
        getRow:  (userId) => rowMap[userId] || null,
        refresh: fetchAll,
        openRepay,
    }), [rowMap, fetchAll, openRepay]);

    return (
        <LoanOwedContext.Provider value={value}>
            {children}
            <MarkRepaidDialog
                open={!!active}
                row={active}
                onClose={() => setActive(null)}
                onDone={fetchAll}
            />
        </LoanOwedContext.Provider>
    );
};

/**
 * Inline orange pill that renders "owes $X" only when the player has an
 * outstanding loan. Clicking it opens the Mark-Repaid dialog so an admin can
 * settle the loan from anywhere a player name is shown.
 *
 * Pass `nonInteractive` to suppress the click handler (e.g. when the badge
 * is rendered inside another clickable element).
 */
export const LoanOwedBadge = ({ userId, className = '', nonInteractive = false }) => {
    const { getOwed, openRepay } = useLoanOwed();
    const owed = getOwed(userId);
    if (!owed || owed <= 0) return null;

    const baseClass = `inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/40 ${className}`;
    const label = `owes $${owed.toFixed(0)}`;
    const title = `Outstanding loan: $${owed.toFixed(2)} — click to mark repaid`;

    if (nonInteractive) {
        return (
            <span className={baseClass} title={`Outstanding loan: $${owed.toFixed(2)}`}
                data-testid={`loan-owed-badge-${userId}`}>
                {label}
            </span>
        );
    }

    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openRepay(userId); }}
            className={`${baseClass} hover:bg-[#FF6B35]/30 hover:border-[#FF6B35]/70 transition-colors cursor-pointer`}
            title={title}
            data-testid={`loan-owed-badge-${userId}`}
        >
            {label}
        </button>
    );
};

export default LoanOwedBadge;
